import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Card from '@mui/material/Card';
import { TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import theme from 'assets/style/theme';

// Common color constants
export const colorCodes = {
    green500: '#008553',
    yellow500: '#FFC107',
    blue500: '#0047BA',
    blueLight: '#005CBC',
    blueLighter: '#E5F5FC',
    white: '#FFFFFF',
    black: '#333',
    gray: '#666',
    grayLight: '#757575',
    grayLighter: '#FBFCFE',
    backgroundOverlay: 'rgba(0,0,0,0.7)',
    backgroundOverlayLight: 'rgba(0,0,0,0.55)',
    iconBackground: 'rgba(123, 194, 227, 0.85)',
};

// Common container styles
export const containerStyle = {
    padding: '20px',
    fontFamily: 'Segoe UI, Arial, sans-serif',
    color: colorCodes.black,
};

export const clusterContainerStyle = {
    ...containerStyle,
    marginTop: '-25px',
};

export const titleStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: colorCodes.blue500,
    textAlign: 'left',
};

export const clusterTitleStyle = {
    ...titleStyle,
    marginTop: '25px',
};

// Table styles
export const tableStyle = {
    minWidth: '610px',
};

export const thStyle = {
    padding: '10px 12px',
    backgroundColor: colorCodes.blueLighter,
    color: colorCodes.blueLight,
    fontWeight: '600',
    textAlign: 'left',
    whiteSpace: 'nowrap',
};

export const thStyleCenter = {
    ...thStyle,
    paddingLeft: '30px',
};

export const tdStyle = {
    padding: '10px 12px',
    backgroundColor: colorCodes.white,
    whiteSpace: 'nowrap',
};

export const sortableCellStyle = {
    ...thStyle,
    cursor: 'pointer',
};

// Badge styles
export const getBadgeStyle = (type, values) => {
    const base = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: '6px',
        fontWeight: 600,
        fontSize: '13px',
        textAlign: 'center',
        minWidth: '90px',
    };

    if (type === 'sparsity') {
        if (values === 'Critical') return { ...base, backgroundColor: colorCodes.yellow500, color: '#000', fontWeight: '500' };
        if (values === 'Normal') return { ...base, backgroundColor: colorCodes.green500, color: colorCodes.white };
    }

    if (type === 'difficulty') {
        if (values === 'Critical') return { ...base, backgroundColor: colorCodes.yellow500, color: '#000' };
        if (values === 'Hard') return { ...base, backgroundColor: colorCodes.yellow500, color: '#000', fontWeight: '500' };
        if (values === 'Very Easy') return { ...base, backgroundColor: colorCodes.green500, color: colorCodes.white };
    }

    return { ...base, backgroundColor: colorCodes.grayLight, color: colorCodes.white };
};

// Status box styles
export const detectionStatusBoxStyle = {
    width: '90px',
    borderRadius: '6px',
    textAlign: 'center',
    padding: '10px',
    color: 'white',
    fontWeight: '500',
};

export const okStatusBoxStyle = {
    ...detectionStatusBoxStyle,
    background: colorCodes.green500,
};

export const warningStatusBoxStyle = {
    width: '90px',
    borderRadius: '6px',
    background: colorCodes.yellow500,
    padding: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '500',
};

export const classificationScoreBoxStyle = {
    width: '90px',
    borderRadius: '6px',
    background: colorCodes.yellow500,
    padding: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '500',
};

export const statusIconStyle = {
    marginRight: '6px',
};

export const badgeIconStyle = {
    marginRight: '4px',
    fontSize: '12px',
};

// Popup and modal styles
export const popupOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: colorCodes.backgroundOverlay,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
};

export const popupContentStyle = {
    background: colorCodes.white,
    padding: '20px',
    borderRadius: '10px',
    maxWidth: '1000px',
    width: '90%',
    position: 'relative',
};

export const popupTitleStyle = {
    marginBottom: '10px',
    textAlign: 'center',
};

export const zoomModalStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
};

export const zoomContentStyle = {
    textAlign: 'center',
    maxWidth: '95vw',
    maxHeight: '90vh',
};

export const zoomImageStyle = {
    maxWidth: '95vw',
    maxHeight: '80vh',
    objectFit: 'contain',
    display: 'block',
    margin: '0 auto',
};

// Image grid styles
export const imageGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '10px',
    marginBottom: '20px',
};

export const imageContainerStyle = {
    position: 'relative',
};

export const imageStyle = {
    width: '100%',
    maxHeight: '150px',
    objectFit: 'cover',
    borderRadius: '6px',
    border: '1px solid #ccc',
    display: 'block',
};

export const driftedImageStyle = {
    ...imageStyle,
    border: '3px solid blue',
};

export const imageButtonStyle = {
    padding: 0,
    border: '1px solid transparent',
    background: 'transparent',
    cursor: 'zoom-in',
    width: '100%',
    overflow: 'hidden',
};

export const downloadButtonStyle = {
    position: 'absolute',
    top: '6px',
    right: '6px',
    background: colorCodes.backgroundOverlayLight,
    color: colorCodes.white,
    padding: '3px 6px',
    fontSize: '12px',
    borderRadius: '4px',
    textDecoration: 'none',
};

export const imageNameStyle = {
    position: 'absolute',
    bottom: '6px',
    left: '6px',
    right: '6px',
    background: colorCodes.backgroundOverlayLight,
    color: colorCodes.white,
    padding: '2px 4px',
    borderRadius: '4px',
    fontSize: '10px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
};

export const driftedLabelStyle = {
    position: 'absolute',
    bottom: '6px',
    right: '6px',
    background: colorCodes.iconBackground,
    color: '#fff',
    padding: '2px 5px',
    borderRadius: '4px',
    fontSize: '12px',
};

// Button styles
export const linkButtonStyle = {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    color: '#070b0fff',
    textDecoration: 'underline',
    cursor: 'pointer',
};

export const disabledLinkButtonStyle = {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    color: '#070b0fff',
    textDecoration: 'underline',
    cursor: 'not-allowed',
};

// Text styles
export const loadingTextStyle = {
    fontSize: '14px',
    color: '#555',
    marginTop: '8px',
};

export const subtitleStyle = {
    fontSize: '10px',
    color: '#666',
};

export const noImagesTextStyle = {
    textAlign: 'center',
    padding: '20px',
};

// Icon button styles
export const closeButtonStyle = {
    position: 'absolute',
    top: 15,
    right: 15,
    backgroundColor: colorCodes.iconBackground,
    color: 'white',
    '&:hover': {
        backgroundColor: '#003a99',
    },
};

// Pagination styles
export const paginationContainerStyle = {
    mt: 2,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
};

export const zoomControlsStyle = {
    mt: 1.5,
    display: 'flex',
    justifyContent: 'center',
    gap: 1.5,
    alignItems: 'center',
};

export const paperStyle = {
    width: 'fit-content',
    maxWidth: '100%',
    overflow: 'hidden',
    mt: 2,
};

export const paperStyleIns = {
    width: '100%',
    overflow: 'hidden',
    mt: 2,
};

export const tablePaginationStyle = {
    rowsPerPageOptions: [4, 10, 25, 100],
};

// Link styles
export const downloadLinkStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
};

// Component layout styles
export const componentStyles = {
    container: {
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'column', md: 'column', lg: 'column', xl: 'row' },
        gap: 2,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
    },
    tableContainer: {
        flex: 0.5,
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
    },
    clusterDetails: {
        flex: 0.6,
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
    },
};

// MUI styled components
export const Item = styled(Paper)(() => ({
    backgroundColor: colorCodes.white,
    boxShadow: '0px 1px 1px #0000004B',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'flex-start',
    height: '50px',
}));

export const StyledCard = styled(Card)({
    width: '286px',
    height: '123px',
    background: colorCodes.white,
    borderRadius: '12px',
    margin: '20px 50px 20px 20px',
});

export const BoldTypography = styled(Typography)({
    fontWeight: '700',
    marginTop: '-30px',
    marginRight: '20px',
    fontSize: '60px',
});

export const StyledTableHeader = styled(TableCell)(() => ({
    color: colorCodes.blueLight,
    font: theme.typography.fontSize.$font_small_bold,
}));

export const StyledTableCell = styled(TableCell)(() => ({
    color: '#3A3A3A',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 'normal',
}));

export const StyledTableRow = styled(TableRow)(() => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
}));

export const StyledDataTable = styled(TableContainer)(() => ({
    padding: '0px 0px 30px 0px',
    borderRadius: '12px',
    width: '98% !important',
}));

export const style = {
    wrapper: {
        background: theme.palette.grey.lighter,
        color: theme.palette.white,
        overflow: 'hidden',
        flexDirection: 'column',
        display: 'flex',
        width: '100%',
        height: '100%',
    },
    dataTable: {
        background: theme.palette.background.$color_background_layer_01,
        borderRadius: theme.spacing(0.75),
        width: '100%',
        boxShadow: '0 0 24px rgba(0, 92, 188, 0.08)',
        marginTop: '20px',
    },
    overViewIamge: {
        marginLeft: '20px',
        marginTop: '15px',
    },
    TypographyText: {
        fontSize: '20px',
    },
    allProjectDiv: {
        display: 'flex',
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
        height: '60px',
    },
    stickyNote: {
        margin: '20px',
    },
    projectText: {
        color: colorCodes.blue500,
        fontSize: '18px',
        fontWeight: '700',
        marginTop: '20px',
    },
    tabStyle: {
        color: '#616161',
        borderBottom: '1px solid #d7e5f8',
        textTransform: 'none',
        '&.Mui-selected': {
            color: colorCodes.blueLight,
        },
        '&:active': {
            borderBottom: '2px solid #005cbc',
        },
    },
    boxStyle: { padding: () => theme.spacing(1.875) },
    innerboxStyle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        height: '3em',
    },
    descTypo: {
        marginTop: '50px',
    },
    breadcrumbIcon: {
        margin: '0 8px',
        width: '16px',
        height: '10px',
    },
    subHeader: {
        background: `${colorCodes.grayLighter} 0% 0% no-repeat padding-box !important`,
        boxShadow: 'rgba(0, 92, 188, 0.15) 0px 0px 24px 0 !important',
        height: '60px',
        position: 'relative',
        zIndex: '99',
    },
    dividerStyle: { color: '#CCCCCC', borderBottom: '0px 1px 0px #00000036', marginY: () => theme.spacing(1.875) },
    notificationWrapper: {
        background: '#FBFCFE',
        minHeight: '60px',
        marginBottom: '20px',
        border: '1px solid #abcbee',
        borderRadius: '4px',
        borderLeft: '5px solid #005CBC',
        padding: '10px 15px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
    },
    notificationHeaderTextStyle: {
        fontSize: '16px',
        fontWeight: '700',
        color: '#0047BA',
    },
    notificationHeaderSubTextStyle: {
        fontSize: '12px',
        fontWeight: '400',
        color: '#3A3A3A',
    },
    bannerStatus: {
        padding: '4px 15px',
        borderRadius: '20px',
        fontWeight: 600,
        fontSize: '13px',
        textAlign: 'center',
        minWidth: '90px',
        backgroundColor: '#EAEAEA',
        color: '3A3A3A',
    },
    bannerStatus_partial_success: {
        backgroundColor: '#D6AC14',
        color: '#000',
    },
    bannerStatus_in_progress: {
        backgroundColor: '#005CBC',
        color: '#FFFFFF',
    },
    bannerStatus_success: {
        backgroundColor: '#017C06',
        color: '#FFFFFF',
    },
    bannerStatus_failed: {
        backgroundColor: '#C12C01',
        color: '#FFFFFF',
    },
    bannerStatus_timed_out: {
        backgroundColor: '#D6AC14',
        color: '#000',
    },
    bannerStatus_not_started: {
        backgroundColor: '#cccccc',
        color: '#3A3A3A',
    },
};
